/**
 * TODO: Figure out a better way of abstracting out these functions. This helper makes it seem like the component structure isn't quite right.
 */
import '@brightspace-ui/core/components/tooltip/tooltip.js';
import '@brightspace-ui/core/components/status-indicator/status-indicator';
import { html } from 'lit';

export const deployFormAttributes = [
  { id: 'branchName', label: 'Branch name', required: true, disabled: () => false },
];

export const tenantFormAttributes = [
  { id: 'loginType', label: 'Login type', required: true, disabled: () => true, type: 'multi-select', attributes: [{ label: 'Magic link', value: 'magic-link' }, { label: 'SAML', value: 'saml' }] },
  { id: 'tenantId', label: 'Tenant ID', required: true, disabled: () => true },
  { id: 'displayName', label: 'Display name', required: true, disabled: () => false },
  { id: 'host', label: 'Sign on URL', required: true, disabled: type => type === 'edit' },
  { id: 'id', label: 'SSO ID', required: true, disabled: () => true },
];
export const userFormAttributes = [
  { id: 'userId', label: 'User ID', required: true, disabled: type => type !== 'add' },
  { id: 'displayName', label: 'Display name', required: false, disabled: () => false },
  { id: 'employeeId', label: 'Employee ID', required: false, disabled: () => false },
  { id: 'firstName', label: 'First name', required: false, disabled: () => false },
  { id: 'lastName', label: 'Last name', required: false, disabled: () => false },
  { id: 'email', label: 'Email', required: false, disabled: () => false },
  { id: 'title', label: 'Title', required: false, disabled: () => false },
  { id: 'department', label: 'Department', required: false, disabled: () => false },
  { id: 'managerName', label: 'Manager name', required: false, disabled: () => false },
  { id: 'managerId', label: 'Manager ID', required: false, disabled: () => false },
  { id: 'win', label: 'WIN', required: false, disabled: () => false },
];

export const inputTemplate = (attr, base, opts = { attrSuffix: 'attr', formType: 'add' }) => {
  const { attrSuffix, formType } = opts;
  const id = `${attr.id}_${attrSuffix}`;
  if (attr.type === 'multi-select') {
    return html`
        <label class="d2l-input-label" for="${id}">${attr.label}</label>
        <select id="${id}" name="${id}" class="d2l-input-select" .value="${base[attr.id]}">
          <option value="">-- Select ${attr.label} --</option>
          ${attr.attributes.map(a => html`
            <option value="${a.value}" .selected=${base[attr.id] === a.value}>${a.label}</option>
          `)}
        </select>
      `;
  }
  if (attr.type === 'select') {
    const currentValue = base[attr.id] || attr.defaultValue || '';
    return html`
        <label class="d2l-input-label" for="${id}">${attr.label}</label>
        <select id="${id}" name="${id}" class="d2l-input-select" .value="${currentValue}">
          ${attr.options.map(option => html`
            <option value="${option.value}" .selected=${currentValue === option.value}>${option.label}</option>
          `)}
        </select>
      `;
  }
  return html`<d2l-input-text ?required="${attr.required}" ?disabled="${attr.disabled(formType)}" label="${attr.label}" name="${id}" id="${id}" type="text" value="${base[attr.id]}"></d2l-input-text>`;
};

export const mapify = (array, id = 'id') => {
  const result = {};
  if (!array) return result;
  array.map(item => {
    result[item[id]] = item;
  });
  return result;
};

export const tenantLoginTypeIndicator = type => {
  return type === 'saml' ?
    html`
              <d2l-status-indicator style="height: fit-content" state="default" text="SAML"></d2l-status-indicator>
            ` :
    html`
              <d2l-status-indicator style="height: fit-content" state="none" text="Magic link"></d2l-status-indicator>
            `;

};

export const tenantTypeIndicator = type => {
  return type === 'provider' ?
    html`
              <d2l-status-indicator style="height: fit-content" state="default" text="provider"></d2l-status-indicator>
            ` : type === 'employer' ?
      html`
                <d2l-status-indicator style="height: fit-content" state="none" text="employer"></d2l-status-indicator>
              ` :
      html`
        <d2l-status-indicator style="height: fit-content" state="none" text="admin"></d2l-status-indicator>
      `;

};

const createStatusIndicator = (environmentId, id, state, text, tooltip = '', bold = false) => html`
  <div style="position: relative; display: inline-block;">
    <d2l-status-indicator
      id="${environmentId}-${id}"
      class="status-indicator"
      state="${state}"
      text="${text}"
      ?bold="${bold}"
      aria-describedby="${tooltip ? id : ''}">
    </d2l-status-indicator>
    ${tooltip
    ? html`<d2l-tooltip for="${environmentId}-${id}" position="bottom">${tooltip}</d2l-tooltip>`
    : ''}
  </div>
`;

/**
 * Generic service status indicator - extensible for any service
 * @param {string} environmentId - Environment ID for unique element IDs
 * @param {string} serviceName - Display name of the service (e.g., 'Wave', 'FUSS')
 * @param {object} status - Status object with { status: 'ok' | 'error', message?: string }
 * @returns {TemplateResult} Lit HTML template for the status indicator
 */
export const getServiceStatusIndicator = (environmentId, serviceName, status) => {
  const serviceKey = serviceName.toLowerCase().replace(/\s+/g, '-');

  if (!status || !status.status) {
    return createStatusIndicator(environmentId, `${serviceKey}-checking`, 'none', `${serviceName}: ...`);
  }

  if (status.status === 'ok') {
    return createStatusIndicator(environmentId, `${serviceKey}-healthy`, 'success', `${serviceName}: Healthy`);
  }

  if (status.status === 'error') {
    return createStatusIndicator(environmentId, `${serviceKey}-error`, 'critical', `${serviceName}: Error`, status.message, true);
  }

  return createStatusIndicator(environmentId, `${serviceKey}-unknown`, 'none', `${serviceName}: Unknown`, status.message);
};

export const getDisplayName = user => {
  const firstLast = `${user.firstName || ''} ${user.lastName || ''}`.trim();
  return firstLast || user.name || user.email || user.thirdPartyUserId || user.guid || user.displayName || 'Unknown';
};

const approvalModelLabels = {
  hybrid: 'Hyb',
  centralized: 'Centralized',
  distributed: 'Distributed',
};

const budgetTypeLabels = {
  caseByCase: 'CxC',
  corporatePolicy: '$',
  percentCoverage: '%',
  reimbursement: 'Reimb',
};

export const getTenantBadges = tenant => {
  const badges = [];

  if (tenant?.approvalModel && approvalModelLabels[tenant.approvalModel]) {
    badges.push({
      text: approvalModelLabels[tenant.approvalModel],
      tooltip: `Approval: ${tenant.approvalModel}`,
      state: 'none',
    });
  }

  const budgetType = tenant?.budgetType || tenant?.budget?.type;
  if (budgetType && budgetTypeLabels[budgetType]) {
    badges.push({
      text: budgetTypeLabels[budgetType],
      tooltip: `Budget: ${budgetType}`,
      state: 'alert',
    });
  }

  if (tenant?.operatingCurrency) {
    badges.push({
      text: tenant.operatingCurrency,
      tooltip: `Currency: ${tenant.operatingCurrency}`,
      state: 'success',
    });
  }

  return badges;
};
